import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useHomeVehicles, getVehicleImage } from '../../../hooks/useVehicles';

const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"%3E%3Crect fill="%23F7F5F0" width="800" height="600"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="18" fill="%239AA8B8"%3ENo Image%3C/text%3E%3C/svg%3E';

const QUICK_FILTERS = [
  { label: 'SUVs',        icon: '🚙', link: '/inventory?type=SUV' },
  { label: 'Trucks',      icon: '🛻', link: '/inventory?type=Truck' },
  { label: 'Under $15K',  icon: '💰', link: '/inventory?maxPrice=15000' },
  { label: 'Under $25K',  icon: '💵', link: '/inventory?maxPrice=25000' },
  { label: 'AWD / 4WD',   icon: '🧭', link: '/inventory?awd=true' },
  { label: 'Low Miles',   icon: '⚡', link: '/inventory?maxMileage=50000' },
];

function StripSkeleton() {
  return (
    <section className="bg-white border-b border-[#EEEBE4]" style={{ paddingTop: '28px', paddingBottom: '32px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-5">
          <div className="h-6 w-48 rounded-lg bg-[#F0EDE6] animate-pulse" />
          <div className="h-5 w-28 rounded-lg bg-[#F0EDE6] animate-pulse" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl overflow-hidden bg-[#F7F5F0] animate-pulse" style={{ height: '200px' }} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function FeaturedInventoryStrip() {
  const { vehicles, loading } = useHomeVehicles(8);

  const featured = useMemo(() => {
    const available = (vehicles || []).filter(v => v.status === 'available');
    // Prioritize lower-priced vehicles as "deals", then fill with others
    const sorted = [...available].sort((a, b) => a.price - b.price);
    return sorted.slice(0, 4);
  }, [vehicles]);

  if (loading) return <StripSkeleton />;

  return (
    <section
      className="bg-white"
      style={{ borderBottom: '1px solid #EEEBE4', paddingTop: '28px', paddingBottom: '36px' }}
      aria-label="Featured inventory"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header row */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <span
              className="w-1 rounded-full flex-shrink-0"
              style={{ height: '22px', background: '#F5C842' }}
              aria-hidden="true"
            />
            <h2
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                fontWeight: 700,
                color: '#1A2332',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
              }}
            >
              Featured Inventory
            </h2>
            {featured.length > 0 && (
              <span
                className="px-2 py-0.5 rounded-full text-xs font-bold"
                style={{ background: '#F5C842', color: '#1A2332', fontFamily: "'Inter', sans-serif" }}
              >
                {featured.length} Available
              </span>
            )}
          </div>
          <Link
            to="/inventory"
            className="flex items-center gap-1.5 text-sm font-semibold cursor-pointer transition-colors hover:opacity-70 whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif", color: '#1A2332', textDecoration: 'none' }}
          >
            View all
            <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">→</span>
          </Link>
        </div>

        {/* Quick filter pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {QUICK_FILTERS.map(f => (
            <Link
              key={f.label}
              to={f.link}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
              style={{
                background: '#F7F5F0',
                border: '1px solid #EEEBE4',
                color: '#374151',
                fontFamily: "'Inter', sans-serif",
                textDecoration: 'none',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#1A2332';
                (e.currentTarget as HTMLAnchorElement).style.color = '#F5C842';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#1A2332';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#F7F5F0';
                (e.currentTarget as HTMLAnchorElement).style.color = '#374151';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#EEEBE4';
              }}
            >
              <span aria-hidden="true">{f.icon}</span>
              {f.label}
            </Link>
          ))}
        </div>

        {/* Vehicle cards */}
        {featured.length === 0 ? (
          /* Empty state — still useful, drives to inventory */
          <div
            className="rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-6 px-6 sm:px-8 py-7"
            style={{ background: 'linear-gradient(135deg, #F7F5F0 0%, #EDE9DF 100%)', border: '1.5px dashed #D8D0BC' }}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0 text-2xl"
                style={{ background: '#1A2332' }}
                aria-hidden="true"
              >
                🚗
              </div>
              <div>
                <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.1rem', fontWeight: 700, color: '#1A2332' }}>
                  New inventory arriving soon
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.8rem', color: '#6B7280', marginTop: '2px' }}>
                  Set a Deal Radar alert and we&apos;ll notify you the moment something matches.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 flex-shrink-0 w-full sm:w-auto justify-center sm:justify-end">
              <Link
                to="/deal-radar"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold whitespace-nowrap cursor-pointer transition-all hover:scale-105 active:scale-95"
                style={{ background: '#1A2332', color: '#F5C842', fontFamily: "'Oswald', sans-serif", fontSize: '0.82rem', letterSpacing: '0.06em', textDecoration: 'none' }}
              >
                📡 Set Alert
              </Link>
              <Link
                to="/broker-services"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold whitespace-nowrap cursor-pointer transition-all hover:scale-105 active:scale-95"
                style={{ background: 'transparent', color: '#1A2332', border: '1.5px solid #1A2332', fontFamily: "'Oswald', sans-serif", fontSize: '0.82rem', letterSpacing: '0.06em', textDecoration: 'none' }}
              >
                🔍 Find Mine
              </Link>
            </div>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            data-product-shop
          >
            {featured.map((vehicle, idx) => {
              const img = getVehicleImage(vehicle) || FALLBACK_IMAGE;
              const label = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
              const monthly = Math.floor(vehicle.price * 0.02);
              const isHot = vehicle.price < 20000;

              return (
                <Link
                  key={vehicle.uuid}
                  to={`/inventory/${vehicle.uuid}`}
                  className="group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
                  style={{ background: '#fff', border: '1px solid #EEEBE4', textDecoration: 'none' }}
                  aria-label={`View ${label} — $${vehicle.price.toLocaleString()}`}
                >
                  {/* Badge */}
                  {isHot && (
                    <div
                      className="absolute top-2.5 left-2.5 z-10 px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ background: '#B91C1C', color: '#fff', fontFamily: "'Inter', sans-serif" }}
                    >
                      🔥 Hot Deal
                    </div>
                  )}
                  {!isHot && idx === 0 && (
                    <div
                      className="absolute top-2.5 left-2.5 z-10 px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ background: '#047857', color: '#fff', fontFamily: "'Inter', sans-serif" }}
                    >
                      ⚡ Just Listed
                    </div>
                  )}

                  {/* Image */}
                  <div className="w-full overflow-hidden" style={{ aspectRatio: '4/3', background: '#F7F5F0' }}>
                    <img
                      src={img}
                      alt={`${label} for sale`}
                      loading={idx < 2 ? 'eager' : 'lazy'}
                      fetchPriority={idx === 0 ? 'high' : idx === 1 ? 'low' : undefined}
                      width={600}
                      height={450}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      onError={e => { (e.currentTarget as HTMLImageElement).src = FALLBACK_IMAGE; }}
                    />
                  </div>

                  {/* Info */}
                  <div className="px-3.5 pt-3 pb-3.5">
                    <p
                      className="font-bold leading-tight mb-1 group-hover:text-[#7a5208] transition-colors line-clamp-2"
                      style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.95rem', color: '#1A2332' }}
                    >
                      {label}
                    </p>
                    <p
                      className="text-xs mb-2.5 truncate"
                      style={{ fontFamily: "'Inter', sans-serif", color: '#6B7280' }}
                    >
                      {vehicle.mileage?.toLocaleString()} mi · {vehicle.transmission}
                    </p>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div
                          className="font-bold"
                          style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.1rem', color: '#1A2332' }}
                        >
                          ${vehicle.price.toLocaleString()}
                        </div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: '#6B7280' }}>
                          ~${monthly}/mo
                        </div>
                      </div>
                      <span
                        className="px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all group-hover:opacity-90 flex-shrink-0"
                        style={{ background: '#1A2332', color: '#F5C842', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.04em' }}
                      >
                        View
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* "See all" CTA card — full-width, high-contrast */}
            <Link
              to="/inventory"
              className="group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 flex flex-col items-center justify-center text-center col-span-1 sm:col-span-2 lg:col-span-1"
              style={{
                background: 'linear-gradient(135deg, #E8B84B 0%, #F5C842 50%, #E8A020 100%)',
                border: '2px solid rgba(232,184,75,0.60)',
                minHeight: '200px',
                textDecoration: 'none',
                boxShadow: '0 4px 24px rgba(232,184,75,0.30)',
              }}
              aria-label="Browse all inventory"
            >
              {/* Subtle pattern overlay */}
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, #1A2332 0px, #1A2332 1px, transparent 1px, transparent 12px)',
                }}
                aria-hidden="true"
              />

              <div className="relative z-10 flex flex-col items-center gap-3 px-6">
                {/* Icon */}
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
                  style={{ background: '#1A2332' }}
                  aria-hidden="true"
                >
                  <i className="ri-car-line" style={{ fontSize: '1.5rem', color: '#E8B84B' }} />
                </div>

                {/* Headline */}
                <div>
                  <p
                    style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#1A2332',
                      letterSpacing: '0.04em',
                      lineHeight: 1.1,
                    }}
                  >
                    See Full Inventory
                  </p>
                  <p
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.75rem',
                      color: 'rgba(26,35,50,0.65)',
                      marginTop: '4px',
                    }}
                  >
                    Filter · Compare · Save
                  </p>
                </div>

                {/* Arrow pill */}
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-full font-bold transition-all duration-300 group-hover:gap-3"
                  style={{
                    background: '#1A2332',
                    color: '#E8B84B',
                    fontFamily: "'Oswald', sans-serif",
                    fontSize: '0.8rem',
                    letterSpacing: '0.08em',
                  }}
                >
                  BROWSE ALL
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" style={{ fontSize: '0.9rem' }} aria-hidden="true" />
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
